import SearchIcon from '@mui/icons-material/Search';
import {
  Badge,
  Box,
  Container,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import { BookingStatus } from '../../../happypaws-shared/src/Booking/models/Booking';
import { useGetClientBookings } from '../../../hooks/bookings/useGetClientBookings';
import { useUpdateBookingStatus } from '../../../hooks/bookings/useUpdateBookingStatus';
import ClientHeader from '../ClientHeader/ClientHeader';
import ClientBookingCard from './components/ClientBookingCard';

const tabLabels = ['All', 'Pending', 'Confirmed', 'Completed', 'Cancelled'];

const getStatusFromTab = (tabIndex: number): BookingStatus | null => {
  switch (tabIndex) {
    case 1:
      return BookingStatus.PENDING;
    case 2:
      return BookingStatus.CONFIRMED;
    case 3:
      return BookingStatus.COMPLETED;
    case 4:
      return BookingStatus.CANCELLED;
    default:
      return null;
  }
};

const ClientBookings: React.FC = () => {
  const { data: bookings, isLoading } = useGetClientBookings();
  const { mutate: updateStatus } = useUpdateBookingStatus();

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const getCountByStatus = (status: BookingStatus | null) => {
    if (!bookings) return 0;
    if (status === null) return bookings.length;
    return bookings.filter((b) => b.status === status).length;
  };

  const filteredBookings = useMemo(() => {
    if (!bookings) return [];
    let filtered = bookings;

    const statusFilter = getStatusFromTab(activeTab);
    if (statusFilter) {
      filtered = filtered.filter((b) => b.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          `${b.sitterFirstName} ${b.sitterLastName}`.toLowerCase().includes(query) ||
          b.pets.some((p) => p.petName.toLowerCase().includes(query)) ||
          b.serviceType.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [bookings, activeTab, searchQuery]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Action handlers
  const handleViewDetails = (id: string) => {
    console.log('Viewing booking details:', id);
  };

  const handleMessageSitter = (id: string) => {
    console.log('Messaging sitter for booking:', id);
  };

  const handleCancelBooking = (id: string) => {
    updateStatus({ bookingId: id, status: BookingStatus.CANCELLED });
  };

  const handleLeaveReview = (id: string) => {
    console.log('Leaving review for booking:', id);
  };

  const handleBookAgain = (id: string) => {
    console.log('Booking again:', id);
  };

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <ClientHeader />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            My Bookings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {filteredBookings.length} booking
            {filteredBookings.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>

        {/* Status Tabs */}
        <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '1rem' },
              },
            }}
          >
            {tabLabels.map((label, index) => (
              <Tab
                key={label}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {label}
                    <Badge
                      badgeContent={getCountByStatus(getStatusFromTab(index))}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: activeTab === index ? 'primary.main' : '#E0E0E0',
                          color: activeTab === index ? 'white' : 'text.secondary',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Search by sitter name, pet name, or service type..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Bookings List */}
        <SkeletonLoader isLoading={isLoading} count={3}>
          {filteredBookings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, backgroundColor: 'white', borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary">
                No bookings found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your filters or search query
              </Typography>
            </Box>
          ) : (
            <Box>
              {filteredBookings.map((booking) => (
                <ClientBookingCard
                  key={booking.id}
                  booking={booking}
                  onViewDetails={handleViewDetails}
                  onMessageSitter={handleMessageSitter}
                  onCancelBooking={handleCancelBooking}
                  onLeaveReview={handleLeaveReview}
                  onBookAgain={handleBookAgain}
                />
              ))}
            </Box>
          )}
        </SkeletonLoader>
      </Container>
    </Box>
  );
};

export default ClientBookings;
